import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  CheckCircle as VerifyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PurchaseService } from '../../../services/purchaseService';
import { PurchaseBill } from '../../../types/purchase';
import { formatCurrency } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';

export const PurchaseBillsList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [bills, setBills] = useState<PurchaseBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBill, setSelectedBill] = useState<PurchaseBill | null>(null);

  useEffect(() => {
    fetchBills();
  }, [page, rowsPerPage, search]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const response = await PurchaseService.getPurchaseBills({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
      });
      setBills(response.purchase_bills || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      enqueueSnackbar('Failed to load purchase bills', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, bill: PurchaseBill) => {
    setAnchorEl(event.currentTarget);
    setSelectedBill(bill);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBill(null);
  };

  const handleView = () => {
    if (selectedBill) {
      navigate(`/admin/purchase/bills/${selectedBill.id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedBill) {
      navigate(`/admin/purchase/bills/${selectedBill.id}/edit`);
    }
    handleMenuClose();
  };

  const handleVerify = async () => {
    if (!selectedBill) return;
    try {
      await PurchaseService.verifyPurchaseBill(selectedBill.id);
      enqueueSnackbar('Bill verified successfully', { variant: 'success' });
      fetchBills();
    } catch (error) {
      enqueueSnackbar('Failed to verify bill', { variant: 'error' });
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      draft: 'default',
      verified: 'primary',
      partially_paid: 'warning',
      paid: 'success',
      overdue: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading && bills.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Purchase Bills</Typography>
        {hasPermission('purchase_bills.create') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/purchase/bills/create')}
          >
            Create Bill
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search bills..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bill Number</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Bill Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.map((bill: any) => (
              <TableRow key={bill.id} hover>
                <TableCell>{bill.bill_number || bill.billNumber || '-'}</TableCell>
                <TableCell>{bill.supplier_name || bill.supplier?.business_name || bill.supplier?.companyName || '-'}</TableCell>
                <TableCell>{bill.po_number || bill.purchaseOrder?.poNumber || '-'}</TableCell>
                <TableCell>{bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{bill.due_date ? new Date(bill.due_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell align="right">{formatCurrency(bill.total_amount || bill.totalAmount || 0)}</TableCell>
                <TableCell align="right">{formatCurrency(bill.balance_amount || bill.balanceAmount || 0)}</TableCell>
                <TableCell>
                  <Chip
                    label={(bill.status || 'draft').toUpperCase().replace('_', ' ')}
                    color={getStatusColor(bill.status || 'draft')}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, bill)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleView}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {hasPermission('purchase_bills.update') && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {selectedBill?.status === 'draft' && hasPermission('purchase_bills.verify') && (
          <MenuItem onClick={handleVerify}>
            <VerifyIcon fontSize="small" sx={{ mr: 1 }} />
            Verify
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
